import {
  AsyncPipe,
  isPlatformBrowser,
  isPlatformServer,
} from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  Inject,
  Input,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ErrorsHostDirective } from '@app/core/forms/directives/errors-host.directive';
import { IfInvalidDirective } from '@app/core/forms/directives/if-invalid.directive';
import { Country } from '@app/entities/countries/interfaces/country';
import { UserBillingInfo } from '@app/entities/users/interfaces/user-billing-info';
import {
  ListingsSelectors,
  LoadCountriesList,
  ProfileSelectors,
} from '@app/store';
import { Store } from '@ngxs/store';
import { filter, Observable, take, tap } from 'rxjs';

declare var google: any;

interface AddressInfo {
  deptBuildingFloor: string;
  zipCode: string;
  city: string;
}

@Component({
  selector: 'app-billing-information',
  templateUrl: './billing-information.component.html',
  styleUrls: ['./billing-information.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    ReactiveFormsModule,
    IfInvalidDirective,
    ErrorsHostDirective,
  ],
})
export class BillingInformationComponent implements AfterViewInit {
  constructor(
    private formBuilder: FormBuilder,
    private store: Store,
    private destroyRef: DestroyRef,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {}

  @Input()
  extendedForm: boolean = false;

  @ViewChild('billingAddress')
  billingAddressRef!: ElementRef<HTMLInputElement>;

  countries$: Observable<Country[] | undefined> = this.store.select(
    ListingsSelectors.countries,
  );

  form!: FormGroup;

  protected billing_info$: Observable<UserBillingInfo | undefined> =
    this.store.select(ProfileSelectors.billing_info);

  private autocomplete?: google.maps.places.Autocomplete;

  private updateOnCountryChange: boolean = false;

  ngOnInit(): void {
    this.store.dispatch(new LoadCountriesList());
    this.form = this.buildForm();
    this.populateForm();
  }

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    const selectedCountry: Country = this.countryIdFC.value;
    if (selectedCountry) {
      this.setAutoComplete(selectedCountry.code);
    }
    this.updateOnCountryChange = true;
  }

  private setAutoComplete(countryCode: string | null): void {
    // If the autocomplete instance already exists, destroy it
    if (this.autocomplete) {
      google.maps.event.clearInstanceListeners(this.autocomplete);
      this.autocomplete = undefined;
    }

    if (!countryCode) {
      return;
    }

    const inputEl = this.billingAddressRef.nativeElement;

    if (!google.maps.places) {
      return;
    }

    this.autocomplete = new google.maps.places.Autocomplete(inputEl, {
      componentRestrictions: { country: [countryCode.toLowerCase()] }, // can I change the country of existing intance or I have to create a new instance of autocomplete
      fields: ['address_components', 'geometry'],
      types: ['address'],
    });

    this.autocomplete!.addListener('place_changed', () => {
      const place = this.autocomplete!.getPlace();
      const details = this.extractAddressDetails(place);
      this.addressInfoFC.setValue(details.deptBuildingFloor);
      this.zipCodeFC.setValue(details.zipCode);
      this.cityFC.setValue(details.city);
    });
  }

  private extractAddressDetails(
    place: google.maps.places.PlaceResult,
  ): AddressInfo {
    let deptBuildingFloor = '';
    let zipCode = '';
    let city = '';

    place.address_components?.forEach((component) => {
      if (
        component.types.includes('subpremise') ||
        component.types.includes('building') ||
        component.types.includes('floor') ||
        component.types.includes('street_number')
      ) {
        deptBuildingFloor = component.long_name;
      }
      if (component.types.includes('postal_code')) {
        zipCode = component.long_name;
      }
      if (component.types.includes('locality')) {
        city = component.long_name;
      }
    });

    return {
      deptBuildingFloor,
      zipCode,
      city,
    };
  }

  toggleAsCompany(): void {
    this.asCompanyFC.setValue(!this.asCompanyFC.value);
  }

  private buildForm(): FormGroup {
    let formFields: any = {
      name: [null],
      surname: [null],
      address: [null],
      address_info: [null],
      zip_code: [null],
      city: [null],
    };

    const formGroup = this.formBuilder.group(formFields);

    const countryIdFC = this.formBuilder.control(null);
    countryIdFC.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((value: number | null) => {
          if (!this.updateOnCountryChange) {
            return;
          }

          if (!value) {
            this.setAutoComplete(null);
          }

          this.countries$
            .pipe(
              takeUntilDestroyed(this.destroyRef),
              take(1),
              tap((countries: Country[] | undefined) => {
                if (!countries) {
                  return this.setAutoComplete(null);
                }

                const country = countries.find((country: Country) => {
                  return country.id === value;
                });

                return this.setAutoComplete(country?.code || null);
              }),
            )
            .subscribe();
        }),
      )
      .subscribe();

    formGroup.addControl('country_id', countryIdFC);

    if (!this.extendedForm) {
      return formGroup;
    }

    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      if (control) {
        control.addValidators(Validators.required);
        control.updateValueAndValidity(); // Ensure the validators are applied immediately
      }
    });

    const asCompanyFC = this.formBuilder.control<boolean>(false);
    const companyNameFC = this.formBuilder.control(null);
    companyNameFC.addValidators(Validators.required);
    const registrationNumberFC = this.formBuilder.control(null);
    registrationNumberFC.addValidators(Validators.required);
    const legalAddressFC = this.formBuilder.control(null);
    legalAddressFC.addValidators(Validators.required);

    asCompanyFC.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((value: boolean | null) => {
          if (value) {
            this.form.addControl('company_name', companyNameFC);
            this.form.addControl('registration_number', registrationNumberFC);
            this.form.addControl('legal_address', legalAddressFC);
          } else {
            this.form.removeControl('company_name');
            this.form.removeControl('registration_number');
            this.form.removeControl('legal_address');
          }
        }),
      )
      .subscribe();

    formGroup.addControl('as_company', asCompanyFC);

    return formGroup;
  }

  private populateForm(): void {
    this.billing_info$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((value: UserBillingInfo | undefined) => !!value),
        tap((userBillingInfo: UserBillingInfo) => {
          this.countryIdFC.setValue(userBillingInfo.country?.id || null);
          this.nameFC.setValue(userBillingInfo.name);
          this.surnameFC.setValue(userBillingInfo.surname);
          this.addressFC.setValue(userBillingInfo.address);
          this.addressInfoFC.setValue(userBillingInfo.address_info);
          this.zipCodeFC.setValue(userBillingInfo.zip_code);
          this.cityFC.setValue(userBillingInfo.city);
        }),
      )
      .subscribe();
  }

  protected get countryIdFC(): FormControl {
    return this.form.get('country_id') as FormControl;
  }

  protected get nameFC(): FormControl {
    return this.form.get('name') as FormControl;
  }

  protected get surnameFC(): FormControl {
    return this.form.get('surname') as FormControl;
  }

  protected get addressFC(): FormControl {
    return this.form.get('address') as FormControl;
  }

  protected get addressInfoFC(): FormControl {
    return this.form.get('address_info') as FormControl;
  }

  protected get zipCodeFC(): FormControl {
    return this.form.get('zip_code') as FormControl;
  }

  protected get cityFC(): FormControl {
    return this.form.get('city') as FormControl;
  }

  protected get asCompanyFC(): FormControl {
    return this.form.get('as_company') as FormControl;
  }

  protected get companyNameFC(): FormControl {
    return this.form.get('company_name') as FormControl;
  }

  protected get registrationNumberFC(): FormControl {
    return this.form.get('registration_number') as FormControl;
  }

  protected get legalAddressFC(): FormControl {
    return this.form.get('legal_address') as FormControl;
  }
}
